import { useCallback, useEffect, useRef, useState } from 'react'
import type {
  CrmChatProviderKey,
  CrmLeadIntegrationChatTab,
} from '@/api/types/crm/crmLeadFunnels.types'
import type { ResponseWrapper } from '@/api/types/base.types'
import { isError } from '@/helpers/checkQuery'
import { getLeadPrimaryProviderConversationId } from '@pages/Admin/CRM/utils/leadFields'
import { buildLeadProviderAccountsFromTabs } from '@pages/Admin/CRM/utils/leadProviderChatAccounts'
import { crmIntegrationChatApi } from '../api/crm-integration-chat.api'
import { mapCrmIntegrationConversationsToChatTabs } from '../lib/mapCrmIntegrationConversationsToChatTabs'
import type {
  CrmIntegrationChatListData,
  CrmIntegrationConversation,
} from '../types/crm-integration-chat.types'

export type UseCrmIntegrationConversationResolveParams = {
  leadId: number
  provider: CrmChatProviderKey
  enabled?: boolean
  /** Вкладки уже известны (карточка / таблица / док). */
  initialTabs?: CrmLeadIntegrationChatTab[]
  /** Предвыбранный тред (клик по конкретному боту / сообществу / чату). */
  initialConversationId?: number | null
}

export type UseCrmIntegrationConversationResolveResult = {
  tabs: CrmLeadIntegrationChatTab[]
  conversationId: number | null
  setConversationId: (id: number) => void
  /** Синхронизация unread вкладки с conversation.unread_count (WS / mark-read). */
  setTabUnreadCount: (conversationId: number, unreadCount: number) => void
  isResolving: boolean
}

const EMPTY_TABS: CrmLeadIntegrationChatTab[] = []

/** Сколько диалогов лида тянем для вкладок (без пагинации). */
const RESOLVE_TABS_PER_PAGE = 100

const buildLeadWithTabs = (
  provider: CrmChatProviderKey,
  tabs: CrmLeadIntegrationChatTab[],
) => ({
  integrations: {
    [provider]: {
      has_chat: tabs.length > 0,
      unread_count: 0,
      last_message_text: null,
      updated_at: null,
      accounts: buildLeadProviderAccountsFromTabs(tabs),
    },
  },
})

const normalizeConversationId = (
  value: number | null | undefined,
): number | null => {
  if (value == null || !Number.isFinite(Number(value)) || Number(value) <= 0) {
    return null
  }
  return Math.floor(Number(value))
}

const resolvePrimaryConversationId = (
  lead: unknown,
  provider: CrmChatProviderKey,
  tabs: CrmLeadIntegrationChatTab[],
  initialConversationId: number | null,
): number | null => {
  const fromInitial = normalizeConversationId(initialConversationId)
  if (fromInitial != null) {
    return fromInitial
  }

  if (tabs.length > 0) {
    return normalizeConversationId(tabs[0]?.conversation_id)
  }

  return getLeadPrimaryProviderConversationId(lead, provider)
}

const parseChatListItems = (res: unknown): CrmIntegrationConversation[] => {
  if (!res || typeof res !== 'object') {
    return []
  }

  // axios response: { data: ResponseWrapper }
  if (isError(res as never)) {
    return []
  }

  const axiosLike = res as { data?: ResponseWrapper<CrmIntegrationChatListData> }
  const payload = axiosLike.data
  const items = payload?.data?.items
  return Array.isArray(items) ? items : []
}

/**
 * Резолв вкладок / conversation_id для мульти-чата (VK / TG / MAX / Wazzup).
 * Если tabs не передали — один запрос crm-integration-chat?lead_id= (без get-lead).
 */
export const useCrmIntegrationConversationResolve = ({
  leadId,
  provider,
  enabled = true,
  initialTabs,
  initialConversationId = null,
}: UseCrmIntegrationConversationResolveParams): UseCrmIntegrationConversationResolveResult => {
  const hasParentTabs = Boolean(initialTabs && initialTabs.length > 0)
  const parentTabs = hasParentTabs ? initialTabs! : EMPTY_TABS

  const [tabs, setTabs] = useState<CrmLeadIntegrationChatTab[]>(() => parentTabs)
  const [conversationId, setConversationIdState] = useState<number | null>(() =>
    resolvePrimaryConversationId(
      buildLeadWithTabs(provider, parentTabs),
      provider,
      parentTabs,
      initialConversationId,
    ),
  )
  const [isResolving, setIsResolving] = useState(false)
  const requestIdRef = useRef(0)
  /** Уже резолвили эту пару provider+lead (даже если tabs пустые — Wazzup). */
  const resolvedKeyRef = useRef<string | null>(
    hasParentTabs ? `${provider}:${leadId}` : null,
  )
  const initialConversationIdRef = useRef(initialConversationId)
  initialConversationIdRef.current = initialConversationId

  const setConversationId = useCallback((id: number) => {
    const next = normalizeConversationId(id)
    if (next == null) {
      return
    }
    setConversationIdState((prev) => (prev === next ? prev : next))
  }, [])

  const setTabUnreadCount = useCallback(
    (targetConversationId: number, unreadCount: number) => {
      const normalizedId = normalizeConversationId(targetConversationId)
      if (normalizedId == null) {
        return
      }

      const nextUnread =
        Number.isFinite(Number(unreadCount)) && Number(unreadCount) > 0
          ? Math.floor(Number(unreadCount))
          : 0

      setTabs((prev) => {
        let changed = false
        const next = prev.map((tab) => {
          if (Number(tab.conversation_id) !== normalizedId) {
            return tab
          }
          if (Number(tab.unread_count ?? 0) === nextUnread) {
            return tab
          }
          changed = true
          return { ...tab, unread_count: nextUnread }
        })
        return changed ? next : prev
      })
    },
    [],
  )

  // Клик по другому диалогу в списке.
  useEffect(() => {
    const next = normalizeConversationId(initialConversationId)
    if (next == null) {
      return
    }
    setConversationIdState((prev) => (prev === next ? prev : next))
  }, [initialConversationId])

  // crm-integration-chat?lead_id= — titles контактов; tabs с карточки — optimistic seed.
  useEffect(() => {
    if (!enabled) {
      return
    }

    const resolveKey = `${provider}:${leadId}`

    if (hasParentTabs) {
      // Не затираем локальный unread (WS / mark-read), если набор вкладок тот же.
      setTabs((prev) => {
        const sameSet =
          prev.length === parentTabs.length &&
          prev.length > 0 &&
          prev.every(
            (tab, index) =>
              Number(tab.conversation_id) ===
              Number(parentTabs[index]?.conversation_id),
          )
        return sameSet ? prev : parentTabs
      })
      setConversationIdState(
        resolvePrimaryConversationId(
          buildLeadWithTabs(provider, parentTabs),
          provider,
          parentTabs,
          initialConversationIdRef.current,
        ),
      )
      // Раньше тут return + resolvedKey → titles «wz1 · канал» так и оставались
      // до F5. Тянем список диалогов, чтобы подставить conversation.title.
    } else if (resolvedKeyRef.current === resolveKey) {
      return
    } else {
      const seededConversationId = normalizeConversationId(
        initialConversationIdRef.current,
      )
      setTabs(EMPTY_TABS)
      setConversationIdState(seededConversationId)
    }

    if (resolvedKeyRef.current === resolveKey) {
      return
    }

    const requestId = ++requestIdRef.current
    setIsResolving(true)

    void (async () => {
      try {
        // Без conversation_id — нужны все диалоги лида для вкладок.
        const res = await crmIntegrationChatApi.getIntegrationChatByLead({
          provider,
          leadId,
          perPage: RESOLVE_TABS_PER_PAGE,
          skipGlobalErrorAlert: true,
        })
        if (requestId !== requestIdRef.current) {
          return
        }

        // Всегда помечаем, иначе при пустых tabs — бесконечный resolve.
        resolvedKeyRef.current = resolveKey

        const items = parseChatListItems(res)
        const nextTabs = mapCrmIntegrationConversationsToChatTabs(
          items,
          provider,
          leadId,
        )
        if (nextTabs.length > 0) {
          setTabs(nextTabs)
          setConversationIdState(
            resolvePrimaryConversationId(
              buildLeadWithTabs(provider, nextTabs),
              provider,
              nextTabs,
              initialConversationIdRef.current,
            ),
          )
        } else if (!hasParentTabs) {
          setTabs(EMPTY_TABS)
        }
      } catch {
        resolvedKeyRef.current = resolveKey
      } finally {
        if (requestId === requestIdRef.current) {
          setIsResolving(false)
        }
      }
    })()
  }, [enabled, hasParentTabs, leadId, parentTabs, provider])

  return {
    tabs,
    conversationId,
    setConversationId,
    setTabUnreadCount,
    isResolving,
  }
}
